import { TranslateService } from '@ngx-translate/core';
import {
  assignIn,
  each,
  get,
  has,
  includes,
  invoke,
  isArray,
  isEmpty,
  isFunction,
  isObject,
  isString,
  map,
  reduce,
  set,
} from 'lodash';

import { SelectOption } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/common.type';
import {
  ComponentStaticText,
  I18nStrType,
  I18nStrTypeWithParams,
} from '../i18n.types';
import { FieldTransformerConfig } from './translate-utils.types';
import { BehaviorSubject, isObservable, rx } from '@app/shared/rx/rx-wrapper';
import { isPromise } from '@app/shared/utils';
import { IEntityFilterItem } from '@app/shared/types';
import { XVII18nService } from '../services';

export const translateSelectOptions = (
  selectOptions: SelectOption[],
  options: {
    translateService: TranslateService;
  },
): SelectOption[] => {
  const { translateService } = options;

  return map(selectOptions, (item) => ({
    ...item,
    text: translateService.instant(item?.text),
  }));
};

export const translateObjectProps = (
  content: ComponentStaticText,
  options: { translateService: TranslateService },
) => {
  const { translateService } = options;

  return reduce(
    content,
    (translatedContent, value, key) => {
      const complicatedI18n = isObject(value) && has(value, 'i18nKey');

      return {
        ...translatedContent,
        [key]:
          isString(value) || complicatedI18n
            ? translateService.instant(
                complicatedI18n
                  ? (value as I18nStrTypeWithParams).i18nKey
                  : (value as I18nStrType),
                get(value, 'interpolateParams'),
              )
            : translateObjectProps(value as ComponentStaticText, options),
      };
    },
    {},
  );
};

export const translateFieldsInObj = <T = Object>(translateParams: {
  obj: T;
  fieldsPathsToTranslate: FieldTransformerConfig;
  transformer: Function;
  originalObj?: T;
  /**
   * is need keep original object reference if the object is not changed after translation.
   * It can be useful for performance optimization in case of big forms with many fields,
   * but should be used with caution as it can lead to unexpected behavior in some cases.
   */
  keepReference?: boolean;
}): T => {
  const {
    obj,
    fieldsPathsToTranslate,
    transformer,
    originalObj,
    keepReference = false,
  } = translateParams || {};
  const { paths, nestedFields } = fieldsPathsToTranslate;

  const clonedObj = keepReference ? obj : { ...obj };

  const omitPropertiesPath = fieldsPathsToTranslate?.omitPropertiesPath ?? '';
  const omitProperties = get(
    clonedObj,
    omitPropertiesPath,
    get(originalObj, omitPropertiesPath, []),
  );

  each(paths, (fieldConfig) => {
    const fieldPath = fieldConfig.path;
    const fieldValue = get(clonedObj, fieldPath);

    if (invoke(fieldConfig, 'skipTransform', clonedObj, originalObj)) return;

    if (fieldConfig.forciblySet) {
      const mapper = fieldConfig?.mappers?.str || transformer;

      return set(clonedObj as Object, fieldPath, mapper(fieldValue));
    }

    const omitPath = fieldsPathsToTranslate.parentField
      ? `${fieldsPathsToTranslate.parentField}.${fieldPath}`
      : fieldPath;

    if (!fieldValue || includes(omitProperties, omitPath)) return;

    if (isArray(fieldValue) && !isEmpty(fieldValue)) {
      set(clonedObj as Object, fieldPath, fieldConfig.mappers.arr(fieldValue));

      return;
    }

    if (isString(fieldValue)) {
      const mapper = fieldConfig?.mappers?.str || transformer;

      return set(clonedObj as Object, fieldPath, mapper(fieldValue));
    }

    if (fieldValue instanceof BehaviorSubject) {
      const mapper = fieldConfig?.mappers?.behaviorSubject || transformer;
      const mappedSubject = new BehaviorSubject(mapper(fieldValue.getValue()));

      fieldValue.subscribe((value) => mappedSubject.next(mapper(value)));

      set(clonedObj as Object, fieldPath, mappedSubject);

      return;
    }

    if (isObservable(fieldValue)) {
      const mapper = fieldConfig?.mappers?.observable;

      if (mapper) {
        return set(clonedObj as Object, fieldPath, mapper(fieldValue));
      }

      set(
        clonedObj as Object,
        fieldPath,
        fieldValue.pipe(rx.map((value) => transformer(value))),
      );

      return;
    }

    if (isPromise(fieldValue)) {
      return set(
        clonedObj as Object,
        fieldPath,
        fieldValue.then((value) => transformer(value)),
      );
    }

    if (isFunction(fieldValue)) {
      const wrapperFunc = (...params): any => {
        const result = fieldValue(...params);

        if (isArray(result) && !isEmpty(result)) {
          return fieldConfig.mappers.arr(result);
        }

        if (isString(result)) {
          return (fieldConfig?.mappers?.str || transformer)(result);
        }

        if (result instanceof BehaviorSubject) {
          const mapper = fieldConfig?.mappers?.behaviorSubject || transformer;
          const mappedSubject = new BehaviorSubject(mapper(result.getValue()));

          result.subscribe((value) => mappedSubject.next(mapper(value)));

          return mappedSubject;
        }

        if (isObservable(result)) {
          const mapper = fieldConfig?.mappers?.observable;

          if (mapper) {
            return set(clonedObj as Object, fieldPath, mapper(result));
          }

          return result.pipe(rx.map((value) => transformer(value)));
        }

        return result;
      };

      return set(clonedObj as Object, fieldPath, wrapperFunc);
    }
  });

  const nestedResult = reduce(
    nestedFields,
    (acc, nestedField) => {
      const initialNestedObject = get(clonedObj, nestedField.parentField, {});
      if (isEmpty(initialNestedObject)) return acc;

      return set(
        acc,
        nestedField.parentField,
        translateFieldsInObj({
          obj: initialNestedObject,
          fieldsPathsToTranslate: nestedField,
          transformer,
          originalObj: originalObj || clonedObj,
          keepReference,
        }),
      );
    },
    {},
  );

  return assignIn(clonedObj, nestedResult) as T;
};

export const translateFilterOptions = (
  options: IEntityFilterItem[],
  i18nService: XVII18nService,
): IEntityFilterItem[] => {
  return map(options, (option) => ({
    ...option,
    text: i18nService.instant(option.text),
  }));
};
